import { PaletteOptions, ThemeOptions } from '@mui/material';
import { defaultThemeOptions } from './';

// Raw F5 design-system tokens. The DS defines more colors than the app maps
// into palette slots today; every token is captured here so wiring a new slot
// (or a future `.hover` / `.light` sub-token) is a one-line reference.
const F5DesignTokens = {
  // Primary
  dodgerBlue: { base: '#4F73FF', hover: '#4164ED', light: '#DBE2FF' },
  brandRed: '#E4002B',
  blueZodiac: '#0F1E57',
  midnight: '#0B1640',
  // Neutral (n100 → n500)
  athens: '#F7F8FA',
  catskill: '#E6E9F3',
  haze: '#CFD4E2',
  gull: '#9EA7B8',
  raven: '#6C778C',
  // Semantic
  emerald: { base: '#35D068', hover: '#25BD57', light: '#D0F5DC' }, // status good
  greenLeaf: '#4F6E22', // graph - darker green for legible text on the light green chip
  pomegranate: { base: '#F94627', hover: '#E93616', light: '#FFDAD4' }, // status error
  amber: { base: '#FFC400', hover: '#FDBB35', light: '#FFF3CC' }, // status warning
  goldenBrown: '#9A6805', // deeper amber-brown for warning chip text
  mediumPurple: { base: '#8777D9', hover: '#6E5CCC', light: '#EBE8FF' }, // status info
  java: { base: '#1ACB9A', hover: '#11BD8D', light: '#CBF5E9' }, // status
} as const;

const palette: PaletteOptions = {
  common: {
    black: F5DesignTokens.midnight,
    white: '#ffffff',
  },
  subtleGray: F5DesignTokens.athens,
  lightGray: F5DesignTokens.catskill,
  gray: F5DesignTokens.gull,
  darkGray: F5DesignTokens.raven,
  divider: F5DesignTokens.catskill,
  background: {
    paper: '#ffffff',
    default: '#ffffff',
    app: F5DesignTokens.athens,
  },
  surface: {
    primary: {
      main: F5DesignTokens.midnight,
    },
    secondary: {
      main: F5DesignTokens.blueZodiac,
    },
    info: {
      main: F5DesignTokens.java.light,
    },
    error: {
      main: F5DesignTokens.pomegranate.light,
    },
    warning: {
      main: F5DesignTokens.amber.light,
    },
    success: {
      main: F5DesignTokens.emerald.light,
    },
    disabled: {
      main: F5DesignTokens.athens,
    },
    successHighlight: {
      main: F5DesignTokens.emerald.base,
    },
    infoHighlight: {
      main: F5DesignTokens.java.base,
    },
    grey: {
      main: F5DesignTokens.gull,
    }
  },
  primary: {
    main: F5DesignTokens.dodgerBlue.base,
    light: F5DesignTokens.dodgerBlue.light,
  },
  secondary: {
    main: F5DesignTokens.blueZodiac,
  },
  link: {
    main: F5DesignTokens.blueZodiac,
    hover: F5DesignTokens.dodgerBlue.base,
  },
  success: {
    main: F5DesignTokens.emerald.base,
  },
  error: {
    main: F5DesignTokens.pomegranate.base,
  },
  warning: {
    main: F5DesignTokens.amber.base,
  },
  info: {
    main: F5DesignTokens.blueZodiac,
  },
  navigation: {
    background: F5DesignTokens.athens,
    text: F5DesignTokens.blueZodiac,
    hover: 'transparent', // F5 menu items have no hover fill (text color shifts instead)
    hoverText: F5DesignTokens.dodgerBlue.base,
    selected: 'transparent',
    selectedText: F5DesignTokens.dodgerBlue.base,
    divider: F5DesignTokens.catskill,
    footer: F5DesignTokens.athens,
    scrollbar: F5DesignTokens.gull,
  },
  chip: {
    default: F5DesignTokens.midnight,
    defaultBackground: F5DesignTokens.haze,
    info: F5DesignTokens.dodgerBlue.hover,
    infoBackground: F5DesignTokens.dodgerBlue.light,
    error: F5DesignTokens.pomegranate.hover,
    warning: F5DesignTokens.goldenBrown,
    success: F5DesignTokens.greenLeaf,
    primary: '#ffffff',
    grey: '#ffffff',
    greyBackground: F5DesignTokens.gull,
    critical: '#ffffff',
    criticalBackground: F5DesignTokens.pomegranate.base,
    outline: F5DesignTokens.gull,
  },
  tooltip: {
    background: F5DesignTokens.blueZodiac,
  },
  toggle: {
    off: F5DesignTokens.gull,
    offHover: F5DesignTokens.raven,
    on: F5DesignTokens.dodgerBlue.base,
    onHover: F5DesignTokens.dodgerBlue.hover,
    offAlternate: F5DesignTokens.blueZodiac,
  },
  // sadly these need to match colors in the portal-ui repo
  portal: {
    background: {
      dark: '#1d1d1e',
      light: '#ffffff',
    },
  },
};

export const F5Theme: ThemeOptions = {
  ...defaultThemeOptions,
  palette,
};
